import type { Components } from 'react-markdown';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { cn } from '@hero/web-lib';
import { EntityLink } from './widgets/EntityLink';
import { parseHeroEntityLink } from './widgets/parseHeroEntityLink';

/**
 * react-markdown's `defaultUrlTransform` strips any href whose URL
 * scheme isn't on its safe-protocol whitelist (`http`, `https`,
 * `mailto`, `tel`, `irc`, `ircs`, `magnet`). Our app-internal
 * `hero://<kind>/<id>` deep links are not on that list, so without
 * this override every `[995182](hero://order/...)` link that reaches
 * the renderer arrives with `href=""`, falls through `parseHeroEntityLink`,
 * and ends up as plain unclickable text — see debug session c66c60
 * (frontend anchor logs).
 */
const allowHeroSchemeUrlTransform = (url: string): string | null => {
  if (url?.toLowerCase().startsWith('hero://')) return url;
  return defaultUrlTransform(url);
};

const PHONE_PATTERN = /^[\d+\s().-]+$/;

const markdownComponents: Components = {
  p: ({ children }) => (
    <p className='mb-3 last:mb-0 leading-7 text-slate-800'>{children}</p>
  ),
  strong: ({ children }) => (
    <strong className='font-semibold text-slate-900'>{children}</strong>
  ),
  ul: ({ children }) => (
    <ul className='mb-3 list-disc space-y-1 ps-5 last:mb-0'>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className='mb-3 list-decimal space-y-1 ps-5 last:mb-0'>{children}</ol>
  ),
  li: ({ children }) => <li className='leading-7 text-slate-800'>{children}</li>,
  hr: () => <hr className='my-4 border-slate-200' />,
  a: ({ href, children }) => {
    const entity = parseHeroEntityLink(href);
    if (entity) {
      const label =
        typeof children === 'string'
          ? children
          : Array.isArray(children)
            ? children.join('')
            : String(children ?? '');
      return (
        <EntityLink
          kind={entity.kind}
          entityId={entity.id}
          label={label}
          variant='inline'
        />
      );
    }

    // Never navigate hero:// in a new tab — the scheme is app-internal only.
    if (href?.trim().toLowerCase().startsWith('hero://')) {
      return <span className='font-medium text-primary-800'>{children}</span>;
    }

    const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
    if (!isExternal) {
      return <span className='font-medium text-slate-800'>{children}</span>;
    }

    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='font-medium text-primary-700 underline decoration-primary-300 underline-offset-2 hover:text-primary-900'
      >
        {children}
      </a>
    );
  },
  code: ({ children }) => (
    <code
      dir='ltr'
      className='rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-slate-800 [unicode-bidi:plaintext]'
    >
      {children}
    </code>
  ),
  table: ({ children }) => (
    <div className='mb-3 overflow-x-auto last:mb-0'>
      <table dir='ltr' className='w-full border-collapse text-start text-sm'>
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => <thead className='border-b border-slate-200'>{children}</thead>,
  tbody: ({ children }) => <tbody className='divide-y divide-slate-100'>{children}</tbody>,
  tr: ({ children }) => <tr>{children}</tr>,
  th: ({ children }) => (
    <th className='px-3 py-2 text-start text-xs font-semibold text-slate-500'>
      {children}
    </th>
  ),
  td: ({ children }) => {
    const text =
      typeof children === 'string'
        ? children
        : Array.isArray(children)
          ? children.join('')
          : '';
    const isPhoneLike = PHONE_PATTERN.test(text.trim());
    return (
      <td
        className={cn(
          'px-3 py-2 text-slate-800',
          isPhoneLike && '[unicode-bidi:plaintext]',
        )}
        dir={isPhoneLike ? 'ltr' : undefined}
      >
        {children}
      </td>
    );
  },
};

/**
 * Renders assistant prose as Markdown (lists, bold, tables, inline code).
 * `hero://client/{id}` links render as tappable inline pills that open
 * the matching entity drawer.
 *
 * `remarkBreaks` is not optional here: the agent's text comes from
 * presenters that lay a message out line by line (one order per line,
 * one lane per line). Stock Markdown folds a single newline into a
 * space, which ran those lines together into one wall of text.
 */
export const AssistantMarkdown = ({ content }: { content: string }) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm, remarkBreaks]}
    components={markdownComponents}
    urlTransform={allowHeroSchemeUrlTransform}
  >
    {content}
  </ReactMarkdown>
);
